import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { addEmployeeBankDetails, clearError } from "./store/employeeBankDetailsSlice";
import { toast } from "react-toastify";
import api from "../../../api";

const AddEditEmployeeBankDetail = ({ show, onClose, employeeIds }) => {
  const dispatch = useDispatch();
  const { addLoading, error } = useSelector(
    (state) => state.employeeBankDetailData
  );

  const [employeeId, setEmployeeId] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [bankName, setBankName] = useState("");
  const [branch, setBranch] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [swiftCode, setSwiftCode] = useState("");

  // Fetch employee ID and name data when the modal opens
  useEffect(() => {
    const fetchEmployeeOptions = async () => {
      try {
        const response = await api.post("/employee-id-name");
        setEmployeeOptions(response.data.data || []); // Assuming the API returns an array of employee objects
        if (employeeIds) {
          // Fetch existing bank details if editing
          const formData = new FormData();
          formData.append("id", employeeIds);
          const detailsResponse = await api.post(
            "/edit-employee-bank-details",
            formData
          );
          const { data } = detailsResponse.data;
          setEmployeeId(data.employee_id || "");
          setBankName(data.bank_name || "");
          setBranch(data.branch || "");
          setAccountNumber(data.account_number || "");
          setIfscCode(data.ifsc_code || "");
          setAccountHolderName(data.bank_account_holder_name || "");
          setSwiftCode(data.swift_code || "");
        }
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    if (show) {
      fetchEmployeeOptions();
    }

    // Clear error when modal closes
    return () => {
      dispatch(clearError());
    };
  }, [show, employeeIds, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const bankDetails = {
      employee_id: employeeId,
      bank_name: bankName,
      branch,
      account_number: accountNumber,
      ifsc_code: ifscCode,
      bank_account_holder_name: accountHolderName,
      swift_code: swiftCode,
    };

    if (employeeIds) {
      bankDetails.id = employeeIds;
    }

    const endpoint = employeeIds
      ? "/update-employee-bank-details"
      : "/add-employee-bank-details";

    const result = await dispatch(
      addEmployeeBankDetails({ endpoint, bankDetails })
    );

    if (addEmployeeBankDetails.fulfilled.match(result)) {
      toast.success(
        employeeIds
          ? "Bank details updated successfully!"
          : "Bank details added successfully!"
      );
      onClose();
    } else if (addEmployeeBankDetails.rejected.match(result)) {
      const serverMessage = result.payload?.message;

      if (typeof serverMessage === "object" && serverMessage !== null) {
        const formattedErrors = Object.entries(serverMessage)
          .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
          .join("\n");
        toast.error(formattedErrors);
      } else {
        toast.error(serverMessage || "Failed to save bank details.");
      }
    }
  };

  useEffect(() => {
    if (!show) {
      setEmployeeId("");
      setBankName("");
      setBranch("");
      setAccountNumber("");
      setIfscCode("");
      setAccountHolderName("");
      setSwiftCode("");
      dispatch(clearError());
    }
  }, [show, dispatch]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg" // Increases modal width
      dialogClassName="custom-modal-width" // Custom class for further width adjustments
    >
      <Modal.Header>
        <Modal.Title>
          {employeeIds ? "Edit Bank Details" : "Add Bank Details"}
        </Modal.Title>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            right: "1rem",
            top: "0rem",
            background: "none",
            border: "none",
            fontSize: "1.5rem",
            color: "#000",
            cursor: "pointer",
          }}
        >
          &times;
        </button>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Select Employee</Form.Label>
                <Form.Control
                  as="select"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  required
                >
                  <option value="">Select Employee</option>
                  {employeeOptions.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Bank Name</Form.Label>
                <Form.Control
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Branch</Form.Label>
                <Form.Control
                  type="text"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Account Number</Form.Label>
                <Form.Control
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>IFSC Code</Form.Label>
                <Form.Control
                  type="text"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Account Holder Name</Form.Label>
                <Form.Control
                  type="text"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>SWIFT Code</Form.Label>
                <Form.Control
                  type="text"
                  value={swiftCode}
                  onChange={(e) => setSwiftCode(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="text-end mt-4">
            <Button variant="primary" type="submit" disabled={addLoading}>
              {addLoading ? (
                <Spinner as="span" animation="border" size="sm" />
              ) : employeeIds ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
          </Form.Group>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditEmployeeBankDetail;


//Bonus , earned leave,  Bonus , earned leave(show in employee), , generate payroll (Approve V1 v2 button add), Loader every screen , Route change in place of add replace import, Error message short on hover show, logo in env